import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const ACTION = { "permission.change": "Toggle", "role.create": "Made", "role.rename": "Renamed", "role.retire": "Retired", "role.restore": "Restored" };

/* ROLE CHANGE HISTORY (his ask): every role's creation, renames, retirements, restores and toggle changes — who, when. */
export default function RoleHistoryPage() {
  const [role, setRole] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    api.get("/admin/roles/history", { params: { role: role === "all" ? "" : role, page, page_size: 50 } })
      .then(({ data }) => setData(data)).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, [role, page]);

  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;
  return (
    <div data-testid="role-history-page" className="space-y-4">
      <Link to="/permissions" data-testid="role-history-back" className="inline-flex items-center gap-1 text-sm text-slate-600"><ArrowLeft className="w-4 h-4" /> Roles & permissions</Link>
      <h1 className="font-display text-2xl font-bold tracking-tight">Role change history</h1>
      <p className="text-sm text-slate-500">Every role made, renamed, retired or restored, and every toggle changed — who did it and when. Newest first.</p>
      <Select value={role} onValueChange={(v) => { setRole(v); setPage(1); }}>
        <SelectTrigger data-testid="role-history-filter" className="w-64 rounded-lg"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all" data-testid="role-history-filter-all">Every role</SelectItem>
          {(data?.roles || []).map((r) => <SelectItem key={r.key} value={r.key} data-testid={`role-history-filter-${r.key.replace(/\s/g, "-")}`}>{r.name}</SelectItem>)}
        </SelectContent>
      </Select>
      {error && <p data-testid="role-history-error" className="text-sm text-[#B01E28]">{error}</p>}
      {data && (
        <div className="sm:hidden space-y-2" data-testid="role-history-cards">
          {data.rows.map((r, i) => (
            <div key={`${r.at}-${i}`} data-testid={`role-history-card-${i}`} className="border border-slate-200 bg-white rounded-lg p-3 text-sm">
              <p><b>{r.role_name}</b> <span className="text-[11px] uppercase font-bold text-slate-500 ml-1">{ACTION[r.action] || r.action}</span></p>
              <p className="mt-0.5">{r.words.join(" · ")}</p>
              <p className="mt-1 text-[11px] text-slate-500 font-mono break-all">{new Date(r.at).toLocaleString()} · {r.by}</p>
            </div>
          ))}
          {!data.rows.length && <p className="text-slate-500 text-sm">Nothing recorded for this role yet.</p>}
        </div>
      )}
      {data && (
        <div className="hidden sm:block border border-slate-200 bg-white rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
              <tr><th className="p-2">When</th><th className="p-2">Who</th><th className="p-2">Role</th><th className="p-2">What</th></tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={`${r.at}-${i}`} data-testid={`role-history-row-${i}`} className="border-t border-slate-100 align-top">
                  <td className="p-2 whitespace-nowrap font-mono text-[12px]">{new Date(r.at).toLocaleString()}</td>
                  <td className="p-2 font-mono text-[12px]">{r.by}</td>
                  <td className="p-2 whitespace-nowrap font-semibold">{r.role_name}</td>
                  <td className="p-2"><span className="text-[11px] uppercase font-bold text-slate-500 mr-1">{ACTION[r.action] || r.action}</span>{r.words.join(" · ")}</td>
                </tr>
              ))}
              {!data.rows.length && <tr><td colSpan={4} data-testid="role-history-empty" className="p-4 text-slate-500">Nothing recorded for this role yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {data && pages > 1 && (
        <div className="flex items-center gap-2 text-sm">
          <Button data-testid="role-history-prev" size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Newer</Button>
          <span data-testid="role-history-page-no">Page {page} of {pages} · {data.total} changes</span>
          <Button data-testid="role-history-next" size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)}>Older</Button>
        </div>
      )}
    </div>
  );
}
